import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Check, ExternalLink } from 'lucide-react';
import { api, money } from '../api';
import { Modal, useToast } from './ui';

/**
 * PRIPOMIENKA „ODKLIKAJTE VYBAVENÉ OBJEDNÁVKY" (.550).
 *
 * Ukáže sa po otvorení systému ľuďom vybraným v Nastavenia → Používatelia,
 * ak majú rozpracované objednávky, ktoré prijali. „Pripomenúť zajtra" ho
 * schová do konca dňa. Vybavenú objednávku sa dá odkliknúť rovno tu.
 */
const dnes = () => new Date().toISOString().slice(0, 10);
const KLUC = 'pripomienka_vybavene_skryte';
const STAV = { nova: 'Nová', potvrdena: 'Potvrdená', objednana: 'Objednaná', pripravena: 'Pripravená na odber' };

export default function PripomienkaObjednavok({ user }) {
  const toast = useToast();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let skryte = null;
    try { skryte = localStorage.getItem(KLUC); } catch { /* prehliadač bez úložiska */ }
    if (skryte === dnes()) return;
    api.get('/api/orders/pripomienka').then((d) => {
      if (d?.ukazat) { setData(d); setOpen(true); }
    }).catch(() => {});
  }, [user?.id]);

  if (!data) return null;
  const zajtra = () => {
    try { localStorage.setItem(KLUC, dnes()); } catch { /* nevadí */ }
    setOpen(false);
  };
  const vybavena = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/api/orders/${o.id}/status`, { status: 'vydana' });
      const zvysok = data.objednavky.filter((x) => x.id !== o.id);
      setData({ ...data, objednavky: zvysok });
      toast.success(`${o.number} je vybavená.`);
      if (!zvysok.length) { setOpen(false); toast.success('Hotovo – žiadna rozpracovaná objednávka.'); }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const stare = data.objednavky.filter((o) => o.dni >= 14).length;

  return (
    <Modal open={open} onClose={zajtra} title="Odkliknite vybavené objednávky" size="max-w-2xl"
      footer={<>
        <button className="btn-secondary" onClick={zajtra}>Pripomenúť zajtra</button>
        <button className="btn-primary" onClick={() => { setOpen(false); nav(`/objednavky?prijal=${user.id}`); }}>
          Otvoriť moje objednávky
        </button>
      </>}>
      <div className="space-y-3 text-sm">
        <div className="flex gap-3 rounded-xl bg-amber-50 p-3 text-amber-900 ring-1 ring-amber-200">
          <ClipboardCheck size={22} className="mt-0.5 shrink-0" />
          <div>
            <p>{data.text}</p>
            <p className="mt-1 text-xs text-amber-800">
              Rozpracovaných máte <b>{data.objednavky.length}</b>{stare ? <>, z toho <b>{stare}</b> starších ako 14 dní</> : null}.
            </p>
          </div>
        </div>
        <div className="max-h-[50vh] divide-y divide-slate-100 overflow-auto rounded-xl ring-1 ring-slate-200">
          {data.objednavky.map((o) => (
            <div key={o.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800">
                  {o.number} <span className="font-normal text-slate-500">· {o.zakaznik || 'bez zákazníka'}</span>
                </div>
                <div className={`text-xs ${o.dni >= 14 ? 'text-red-600' : 'text-slate-500'}`}>
                  {STAV[o.status] || o.status} · {o.order_date ? new Date(`${o.order_date}T00:00:00`).toLocaleDateString('sk-SK') : '—'}
                  {o.dni > 0 ? ` · pred ${o.dni} ${o.dni === 1 ? 'dňom' : 'dňami'}` : ' · dnes'}
                  {Number(o.total_gross) > 0 ? ` · ${money(o.total_gross)} s DPH` : ''}
                </div>
              </div>
              <button className="btn-secondary !px-2 !py-1 text-xs" title="Otvoriť objednávku"
                onClick={() => { setOpen(false); nav(`/objednavky/${o.id}`); }}>
                <ExternalLink size={14} />
              </button>
              <button className="btn-primary !px-2.5 !py-1 text-xs" disabled={busy === o.id} onClick={() => vybavena(o)}>
                <Check size={14} /> {busy === o.id ? '…' : 'Vybavená'}
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">Tie, ktoré ešte vybavené nie sú (čaká sa na tovar, zákazník si nevyzdvihol…), nechajte tak.</p>
      </div>
    </Modal>
  );
}

/** Nastavenie pre administrátora (Nastavenia → Používatelia). */
export function PripomienkaNastavenie() {
  const toast = useToast();
  const [n, setN] = useState(null);
  const [ludia, setLudia] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/api/orders/pripomienka/nastavenie').then(setN).catch(() => {});
    api.get('/api/orders/zamestnanci').then(setLudia).catch(() => {});
  }, []);
  if (!n) return null;
  const prepni = (id) => setN({ ...n, user_ids: n.user_ids.includes(id) ? n.user_ids.filter((x) => x !== id) : [...n.user_ids, id] });
  const uloz = async () => {
    setBusy(true);
    try { setN(await api.put('/api/orders/pripomienka/nastavenie', n)); toast.success('Pripomienka uložená.'); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-p space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-800">Pripomienka: odkliknúť vybavené objednávky</h3>
          <p className="text-xs text-slate-500">Okno sa ukáže vybraným ľuďom po otvorení systému, keď majú rozpracované objednávky, ktoré prijali. „Pripomenúť zajtra" ho schová do konca dňa.</p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-sm">
          <input type="checkbox" checked={n.zapnuta} onChange={(e) => setN({ ...n, zapnuta: e.target.checked })} /> zapnutá
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        {ludia.map((u) => (
          <label key={u.id} className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm ring-1 ${n.user_ids.includes(u.id) ? 'bg-brand-50 ring-brand-300' : 'ring-slate-200'}`}>
            <input type="checkbox" checked={n.user_ids.includes(u.id)} onChange={() => prepni(u.id)} /> {u.name}
          </label>
        ))}
      </div>
      <textarea className="input min-h-[70px] w-full text-sm" value={n.text} onChange={(e) => setN({ ...n, text: e.target.value })} />
      <div className="flex justify-end"><button className="btn-primary" disabled={busy} onClick={uloz}>{busy ? 'Ukladám…' : 'Uložiť'}</button></div>
    </div>
  );
}
